// Weather-Summary-Store fuer das Dashboard-Tile. Backend ist
// /v2-api/weather (Open-Meteo via internal/weather, 15-min-Cache).
//
// Wir schauen nur auf "current" -- fuer das Tile reicht Temperatur +
// Bedingung. Detail-Page (Forecast/Hourly) baut spaeter ihren eigenen
// Konsument.
//
// Polling: 5 min im Frontend. Backend cached 15 min, also fuehrt das
// nicht zu mehr OpenMeteo-Aufrufen -- der zweite Tab wird
// stale-served.

import { SvelteMap } from "svelte/reactivity";
import { registerRevalidator } from "./revalidate.svelte";
import type { IconName } from "./Icon.svelte";
import { loadPersisted, persist } from "./persistent-summary.svelte";

const STORAGE_KEY = "weather";

export interface WeatherCurrent {
  temp: number | null;
  feels_like: number | null;
  humidity: number | null;
  wind_speed: number | null;
  wind_dir: number | null;
  cloud_cover: number | null;
  pressure: number | null;
  is_day: number | null;
  weather_code: number;
  description: string;
  icon: string;
}

export interface WeatherHourly {
  times: string[]; // ISO local "2026-05-09T14:00" pro Eintrag
  temps: number[];
  codes: number[];
  precip: number[]; // %
  wind: number[];
}

export interface WeatherDaily {
  date: string;
  temp_max: number | null;
  temp_min: number | null;
  precipitation: number;
  sunrise: string;
  sunset: string;
  weather_code: number;
  description: string;
  icon: string;
  uv_index: number;
  wind_max: number;
}

// LightningStatus spiegelt internal/lightning.Status: "connecting" deckt
// Erst-Connect + Auto-Reconnect ab (am oeffentlichen Broker u.U. Minuten),
// "off" = Subscriber laeuft nicht. UI labelt nur "off"/Reconnect-Verlust
// als Problem, die Connect-Phase als neutral "verbinde…".
export type LightningStatus = "off" | "connecting" | "connected";

export interface WeatherLightning {
  count_30m: number;
  nearest_km: number | null;
  strikes: { dist: number; ts: number }[];
  track_radius_km: number;
  alert_radius_km: number;
  status: LightningStatus;
}

export interface WeatherSummary {
  current: WeatherCurrent;
  hourly?: WeatherHourly;
  daily: WeatherDaily[];
  lightning?: WeatherLightning;
  latitude?: number;
  longitude?: number;
  error?: string;
}

const _summary = new SvelteMap<string, WeatherSummary>();
let _started = false;
let _timer: ReturnType<typeof setInterval> | undefined;

interface RawCurrent {
  temp: number | null;
  feels_like: number | null;
  humidity: number | null;
  wind_speed: number | null;
  wind_dir: number | null;
  cloud_cover: number | null;
  pressure?: number | null;
  is_day: number | null;
  weather_code: number;
  description: string;
  icon: string;
}

interface RawSnapshot {
  current: RawCurrent;
  daily?: WeatherDaily[];
  hourly_times?: string[];
  hourly_temps?: number[];
  hourly_codes?: number[];
  hourly_precip?: number[];
  hourly_wind?: number[];
  lightning?: WeatherLightning;
  latitude?: number;
  longitude?: number;
  error?: string;
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/weather", { credentials: "include" });
    if (!r.ok) return;
    const snap = (await r.json()) as RawSnapshot;
    const hourly: WeatherHourly | undefined =
      snap.hourly_times && snap.hourly_temps && snap.hourly_codes
        ? {
            times: snap.hourly_times,
            temps: snap.hourly_temps,
            codes: snap.hourly_codes,
            precip: snap.hourly_precip ?? [],
            wind: snap.hourly_wind ?? [],
          }
        : undefined;
    const next: WeatherSummary = {
      current: { ...snap.current, pressure: snap.current.pressure ?? null },
      hourly,
      daily: snap.daily ?? [],
      lightning: snap.lightning,
      latitude: snap.latitude,
      longitude: snap.longitude,
      error: snap.error,
    };
    _summary.set("current", next);
    persist(STORAGE_KEY, next);
  } catch {
    // ignore -- naechster Tick versucht es wieder
  }
}

export function startWeatherSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  const cached = loadPersisted<WeatherSummary>(STORAGE_KEY);
  if (cached) _summary.set("current", cached);
  void refresh();
  _timer = setInterval(refresh, 5 * 60_000);
}

export function stopWeatherSummary(): void {
  _started = false;
  if (_timer) {
    clearInterval(_timer);
    _timer = undefined;
  }
  _summary.clear();
}

export function getWeatherSummary(): WeatherSummary | undefined {
  return _summary.get("current");
}

// weatherIconName mappt den OpenMeteo-Icon-Key auf einen Lucide-Icon-
// Namen aus Icon.svelte. Day/Night wird beruecksichtigt: bei klarem
// Himmel zeigt's nachts den Mond statt der Sonne. isDay = 1 (Tag) /
// 0 (Nacht) / null (unbekannt -> Tag-Variante).
// Mappt einen WMO-Code direkt auf ein Icon. Spart einen Roundtrip
// ueber die String-Keys, fuer Hourly-Forecast wo wir nur die Codes
// haben. Day/Night via Parameter, sonst Tag-Default.
export function weatherIconFromCode(code: number, isDay: number | null = 1): IconName {
  if (code === 0) return weatherIconName("clear", isDay);
  if (code <= 2) return weatherIconName("partly_cloudy", isDay);
  if (code === 3) return weatherIconName("cloudy", isDay);
  if (code === 45 || code === 48) return weatherIconName("fog", isDay);
  if (code >= 51 && code <= 67) return weatherIconName("rain", isDay);
  if ((code >= 80 && code <= 82) || (code >= 95 && code <= 99))
    return weatherIconName("rain", isDay);
  if ((code >= 71 && code <= 77) || code === 85 || code === 86)
    return weatherIconName("snow", isDay);
  return weatherIconName("cloudy", isDay);
}

export function weatherIconName(key: string, isDay: number | null = 1): IconName {
  const day = isDay !== 0;
  switch (key) {
    case "clear":
      return day ? "sun" : "moon";
    case "partly_cloudy":
    case "cloudy":
      return "cloud";
    case "fog":
      return "cloud-fog";
    case "drizzle":
    case "rain":
    case "heavy_rain":
    case "thunderstorm":
      return "cloud-rain";
    case "snow":
      return "cloud-snow";
    default:
      return "cloud";
  }
}
